import React, { useEffect, useState } from "react";
import { api, formatIDR } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const COLUMNS = [
  { key: "new_inquiry", label: "New Inquiry", color: "bg-blue-50 border-blue-200 text-blue-700" },
  { key: "confirmed", label: "Confirmed", color: "bg-amber-50 border-amber-200 text-amber-700" },
  { key: "deposit_paid", label: "Deposit Paid", color: "bg-emerald-50 border-emerald-200 text-emerald-700" },
  { key: "completed", label: "Completed", color: "bg-zinc-100 border-zinc-200 text-zinc-700" },
  { key: "cancelled", label: "Cancelled", color: "bg-red-50 border-red-200 text-red-700" },
];

export default function EventsCRM() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");

  const load = () => api.get("/reservations").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await api.put(`/reservations/${id}`, { status });
    toast.success("Updated");
    load();
    if (selected && selected.id === id) setSelected({ ...selected, status });
  };

  const saveNotes = async () => {
    await api.put(`/reservations/${selected.id}`, { notes });
    toast.success("Notes saved");
    setSelected({ ...selected, notes });
    load();
  };

  const byStatus = (s) => items.filter((i) => i.status === s);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>Events / CRM</h1>
      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="crm-kanban">
        {COLUMNS.map((col) => (
          <div key={col.key} className="w-72 shrink-0">
            <div className={`px-3 py-2 rounded-t-md border ${col.color} text-xs uppercase tracking-wider font-medium flex items-center justify-between`}>
              <span>{col.label}</span>
              <span className="font-mono">{byStatus(col.key).length}</span>
            </div>
            <div className="bg-white border border-t-0 border-zinc-200 rounded-b-md p-2 space-y-2 min-h-[400px]">
              {byStatus(col.key).map((r) => (
                <button key={r.id} onClick={() => { setSelected(r); setNotes(r.notes || ""); }}
                  data-testid={`crm-card-${r.id}`}
                  className="w-full text-left p-3 rounded-md border border-zinc-200 hover:border-zinc-400 transition-colors bg-zinc-50/50">
                  <p className="text-xs font-mono text-zinc-500">{r.booking_number}</p>
                  <p className="text-sm font-medium mt-1">{r.customer_name}</p>
                  <p className="text-xs text-zinc-500">{r.event_type}</p>
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="font-mono">{r.event_date}</span>
                    <Badge variant="outline" className="text-[10px]">{r.package}</Badge>
                  </div>
                  <p className="mt-1 text-xs font-mono text-zinc-700">{formatIDR(r.quoted_price)}</p>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-xl">
          {selected && (
            <>
              <DialogHeader><DialogTitle>{selected.booking_number}</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm">
                <div><span className="text-zinc-500">Customer:</span> {selected.customer_name} · {selected.customer_email} · {selected.customer_phone}</div>
                <div><span className="text-zinc-500">Type:</span> {selected.event_type}</div>
                <div><span className="text-zinc-500">Date:</span> <span className="font-mono">{selected.event_date} · {selected.start_time}–{selected.end_time}</span></div>
                <div><span className="text-zinc-500">Guests:</span> {selected.guest_count} · Package: {selected.package} · Quote: <span className="font-mono">{formatIDR(selected.quoted_price)}</span></div>
                {selected.special_requests && <div><span className="text-zinc-500">Requests:</span> {selected.special_requests}</div>}
                <div>
                  <label className="text-xs uppercase text-zinc-500">Status</label>
                  <Select value={selected.status} onValueChange={(v) => updateStatus(selected.id, v)}>
                    <SelectTrigger data-testid="crm-status-select" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {COLUMNS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs uppercase text-zinc-500">Internal Notes</label>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" data-testid="crm-notes" />
                  <Button size="sm" className="mt-2" onClick={saveNotes} data-testid="crm-save-notes">Save Notes</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
